package pl.decks.personal.components.layouts.site

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.navigation.LinkStyle
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.GlobalStylesheet
import pl.decks.personal.styles.SiteLayout

object SiteLayoutStylesheet {
    object Vars {
        val hfBackgroundColor by StyleVariable<Color>(Colors.Transparent)
        val hfBorderColor by StyleVariable<Color>()
        val hfTopBottomPadding by StyleVariable(SiteLayout.Space.Medium)
        val logoFontSize by StyleVariable(FontTypography.Size.Large)
        val logoIconSize by StyleVariable(FontTypography.Size.Relative.MuchLarger)

        val approxScreenSpace by StyleVariable<CSSLengthOrPercentageNumericValue>()
    }

    object Header {
        sealed interface Kind : ComponentKind

        val Style = CssStyle.base<Kind> {
            val palette = colorMode.toPalette()
            Modifier
                .fillMaxWidth()
                .backgroundColor(Vars.hfBackgroundColor.value())
                .padding(topBottom = Vars.hfTopBottomPadding.value(), leftRight = SiteLayout.Space.Small)
                .borderBottom(SiteLayout.Line.Hairline, LineStyle.Solid, Vars.hfBorderColor.value(palette.border))
                .gap(SiteLayout.Space.Medium)
        }

        val CollapsedVariant = Style.addVariantBase {
            Modifier.borderRadius(SiteLayout.Rounding.none)
        }
        val ExpandedVariant = Style.addVariantBase {
            Modifier.borderRadius(bottomLeft = SiteLayout.Rounding.Large, bottomRight = SiteLayout.Rounding.Large)
        }

        val Bar = CssStyle.base {
            Modifier.fillMaxWidth().padding(leftRight = SiteLayout.Space.Small)
        }

        val Logo = FontTypography.mono.extendedByBase {
            Modifier
                .fontSize(Vars.logoFontSize.value())
                .fontWeight(FontTypography.Weight.Bold)
                .gap(SiteLayout.Space.XXSmall)
        }
        val LogoIcon = CssStyle.base {
            Modifier.fontSize(Vars.logoIconSize.value())
        }

    }

    object Menu {
        val Inline = CssStyle.base {
            Modifier.padding(leftRight = SiteLayout.Space.XLarge).gap(SiteLayout.Space.XXSmall)
        }
        val Collapsable = CssStyle.base {
            Modifier.fillMaxWidth().overflow(Overflow.Hidden)
        }
        val EntryLinkVariant = LinkStyle.addVariant {
            val highlight = colorMode.toSitePalette().highlight.toRgb()
            base {
                Modifier
                    .padding(
                        leftRight = SiteLayout.Space.Small,
                        topBottom = SiteLayout.Space.XSmall,
                    )
                    .transition(Transition.of("background-color", duration = TransitionDurationVars.Normal.value()))
                    .textAlign(TextAlign.End)
                    .borderRadius(SiteLayout.Rounding.Medium)
            }
            hover {
                Modifier.backgroundColor(highlight.copyf(alpha = 0.75f))
            }
            active {
                Modifier.backgroundColor(highlight)
            }
        }
        val EntryLinkSelectedVariant = LinkStyle.addVariantBase {
            val highlight = colorMode.toSitePalette().highlight.toRgb()
            Modifier.backgroundColor(highlight.copyf(alpha = 0.5f))
        }
    }

    object Page {
        val Content = CssStyle {
            base {
                Modifier
                    .fillMaxWidth()
                    .maxWidth(SiteLayout.Breakpoint.md)
                    .padding(leftRight = SiteLayout.Space.Small, topBottom = SiteLayout.Space.XLarge)
            }
            Breakpoint.MD {
                Modifier.padding(leftRight = SiteLayout.Space.XLarge, topBottom = SiteLayout.Space.XXXLarge)
            }
        }
        val Accented = GlobalStylesheet.Screen.extendedByBase {
            val sitePalette = colorMode.toSitePalette()
            val tbPadding = SiteLayout.Space.Medium
            val logoFontSize = FontTypography.Size.Large
            val logoIconSize = 1.5
            val fhHeight = 4 * tbPadding +
                    FontTypography.LineHeight.Normal * logoFontSize * logoIconSize +
                    FontTypography.LineHeight.Normal * (FontTypography.Size.Normal + FontTypography.Size.XSmall)
            Modifier
                .setVariable(Vars.hfBackgroundColor, sitePalette.surface)
                .setVariable(Vars.hfBorderColor, sitePalette.accents.primary)
                .setVariable(Vars.hfTopBottomPadding, tbPadding)
                .setVariable(Vars.logoFontSize, logoFontSize)
                .setVariable(Vars.logoIconSize, logoIconSize.em)
                .setVariable(Vars.approxScreenSpace, 100.vh - fhHeight)
        }
        val HeaderlessScreen = CssStyle.base {
            Modifier.fillMaxWidth().overflow(Overflow.Auto)
        }
    }

    object Footer {
        val Style = CssStyle.base {
            val palette = colorMode.toPalette()
            Modifier
                .fillMaxWidth()
                .backgroundColor(Vars.hfBackgroundColor.value())
                .padding(topBottom = Vars.hfTopBottomPadding.value(), leftRight = SiteLayout.Space.XLarge)
                .borderTop(SiteLayout.Line.Hairline, LineStyle.Solid, Vars.hfBorderColor.value(palette.border))
        }
        val CreatedWith = FontTypography.serif
        val Copyright = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.XSmall)
                .fontWeight(FontTypography.Weight.Light)
        }
    }
}
